/* eslint-disable no-use-before-define */
import { useState, useEffect, useRef } from 'react';
import { Input } from '@material-ui/core';
import classnames from 'classnames';

import { useHandleStyles } from './styles';
import { getIsInputValueValid, getValidatedValue } from './utils';
import {
  HEAD_PLACEMENT,
  HORIZONTAL_ORIENTATION,
  HOVER_AREA_HEIGHT,
  HOVER_AREA_WIDTH_BY_DIMENSION,
  VERTICAL_ORIENTATION,
} from './constants';

import { LeftIcon, RightIcon } from '~/icons';

const isBuggySymbolEntered = event => {
  const value = event?.target?.value;
  const floatValue = value ? parseFloat(value) : 0;
  const isValidValue =
    floatValue >= Number.MIN_SAFE_INTEGER && floatValue < Number.MAX_SAFE_INTEGER;
  const char = event?.nativeEvent?.data;
  const pointCount = value.split('.').length - 1;
  const isValidChar = !char || char === '.' || char === '-' || (char >= '0' && char <= '9');
  const isNotValidMinus = char === '-' && value === '';
  return !isValidValue || !isValidChar || pointCount > 1 || isNotValidMinus;
};

/*
interface HandleProps {
  value: number | string;
  orientation: 'vertical' | 'horizontal';
  placement: 'head' | 'tail';
  onChange: (newValue: number) => void;
  min: number;
  max: number;
  minimal?: boolean;
}
*/
export const Handle = ({
  value,
  orientation,
  placement,
  dimension,
  readOnly,
  onChange,
  min,
  max,
  displayFormatter,
  isInsideArrowsVisible,
  arrowsOutsideConfig,
  classes,
  minimal,
  'data-testid': dataTestId,
}) => {
  const [hasError, setHasError] = useState(false);
  const [internal, setInternal] = useState(value);
  const inputRef = useRef(null);
  const isHorizontalOrientation = orientation === HORIZONTAL_ORIENTATION;
  const isVerticalOrientation = orientation === VERTICAL_ORIENTATION;
  const isHeadPlacement = placement === HEAD_PLACEMENT;

  const styles = useHandleStyles({
    distance: arrowsOutsideConfig?.distance,
    minimal,
    hoverAreaHeight: isHorizontalOrientation
      ? HOVER_AREA_WIDTH_BY_DIMENSION[dimension]
      : HOVER_AREA_HEIGHT,
    hoverAreaWidth: isHorizontalOrientation
      ? HOVER_AREA_HEIGHT
      : HOVER_AREA_WIDTH_BY_DIMENSION[dimension],
  });

  const onChangeValue = e => {
    const newValue = e.target.value;
    if (isBuggySymbolEntered(e)) {
      return;
    }

    setInternal(newValue);
    if (hasError) setHasError(false);
  };

  const handleKeyDown = ({ keyCode, target }) => {
    if (keyCode !== 13) return;
    handleValueSubmit(target.value);
  };

  const handleBlur = ({ target }) => {
    handleValueSubmit(target.value);
  };

  const handleValueSubmit = value => {
    if (readOnly) {
      return;
    }

    const formattedValue = Number(value);
    const isInputValid = getIsInputValueValid(formattedValue);
    if (!isInputValid) {
      setHasError(true);
      return;
    }

    onChange(getValidatedValue(formattedValue, min, max));
  };

  useEffect(() => {
    setInternal(value);
    if (hasError) setHasError(false);
  }, [value]);

  return (
    <div
      className={classnames(styles.root, classes.handleRoot, styles[dimension], {
        [styles.horizontalHeadRoot]: isHorizontalOrientation && isHeadPlacement,
        [styles.horizontalTailRoot]: isHorizontalOrientation && !isHeadPlacement,
        [styles.verticalHeadRoot]: isVerticalOrientation && isHeadPlacement,
        [styles.verticalTailRoot]: isVerticalOrientation && !isHeadPlacement,
        [styles.minimalHandleRoot]: minimal,
      })}
    >
      <LeftIcon
        size={16}
        className={classnames(styles.leftArrow, {
          [styles.reversedLeftArrow]: isVerticalOrientation,
          [styles.isInvisible]:
            (isInsideArrowsVisible && !isHeadPlacement) ||
            (isHeadPlacement && arrowsOutsideConfig?.isLeftArrowInvisible),
        })}
        fill="white"
      />

      <div
        className={classnames(styles.handle, classes.handle, {
          [styles.horizontalHeadHandle]: isHorizontalOrientation && isHeadPlacement,
          [styles.horizontalTailHandle]: isHorizontalOrientation && !isHeadPlacement,
          [styles.verticalHeadHandle]: isVerticalOrientation && isHeadPlacement,
          [styles.verticalTailHandle]: isVerticalOrientation && !isHeadPlacement,
          [styles.minimalHandle]: minimal,
          [styles.minimalVerticalHandle]: minimal && isVerticalOrientation,
        })}
        data-testid={dataTestId}
      >
        {!minimal && (
          <Input
            inputProps={{
              'data-testid': 'handle-input',
            }}
            readOnly={readOnly}
            inputRef={inputRef}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            classes={{
              root: classnames(styles.inputRoot, hasError && styles.hasError),
            }}
            value={readOnly ? displayFormatter(internal) : internal}
            onChange={onChangeValue}
            disableUnderline
            error={!(Number.isFinite(+internal) && +internal >= min && +internal <= max)}
          />
        )}
      </div>

      <RightIcon
        size={16}
        className={classnames(styles.rightArrow, {
          [styles.reversedRightArrow]: isVerticalOrientation,
          [styles.isInvisible]:
            (isInsideArrowsVisible && isHeadPlacement) ||
            (!isHeadPlacement && arrowsOutsideConfig?.isRightArrowInvisible),
        })}
        fill="white"
      />
    </div>
  );
};
